import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useMediaQuery } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PassOut, PassStatus } from '../../api';
import { useLanguage } from '../../utils/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../utils/getDateAndTimeFormat';
import { formatSlotLabel, sortScheduleSelections } from '../../shared/passScheduleSelector/utils';
import { useManagePasses } from '../ManagePassesContext';
import {
  canBuyPassAgain,
  getCurrentScheduleSelections,
  getDaysUntilExpiry,
  getEffectiveNumPunches,
  getExpirationDate,
  getPassOrderPage,
  getPassOrderPageName,
  getUsedPunches,
  hasPassStarted,
  isFixedScheduleMonthly,
  isMembership,
} from '../utils/passUtils';
import { PunchProgress } from './PunchProgress';
import { MembershipSummary } from './MembershipSummary';
import { getStatusChip } from './passStatusChip';
import { BuyAgainSheet } from './BuyAgainSheet';

type PassCardProps = {
  pass: PassOut;
};

export const PassCard = ({ pass }: PassCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { account, passes } = useManagePasses();
  const language = useLanguage();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const { longDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);
  const [buyAgainOpen, setBuyAgainOpen] = useState(false);
  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  const numPunches = getEffectiveNumPunches(pass, passOrderPage);
  const used = getUsedPunches(pass);
  const passName = getPassOrderPageName(passOrderPage, language, t);
  const showBuyAgain = canBuyPassAgain(pass, passOrderPage, account, passes);
  const membership = isMembership(passOrderPage);
  const weeklySlots =
    membership && isFixedScheduleMonthly(passOrderPage?.pass_type)
      ? sortScheduleSelections(getCurrentScheduleSelections(pass))
      : [];

  return (
    <>
      <Grid
        container
        flexDirection="column"
        gap={1.5}
        onClick={() => navigate(`/passes/pass/${pass.id}`)}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          p: isSmallScreen ? 2 : 2.5,
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          '&:active': { backgroundColor: 'action.hover' },
          '&:hover': { backgroundColor: 'action.hover' },
          transition: 'background-color 0.15s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
        <Grid container justifyContent="space-between" alignItems="center" gap={1}>
          <Grid item xs>
            <Typography variant="subtitle1" fontWeight={600}>
              {passName}
            </Typography>
          </Grid>
          {getStatusChip(pass, numPunches, passOrderPage, t)}
        </Grid>
        {membership ? (
          weeklySlots.length > 0 ? (
            <Grid container gap={0.75}>
              {weeklySlots.map((sel) => (
                <Chip
                  key={`${sel.order_page_id}-${sel.weekday}-${sel.start_time}`}
                  size="small"
                  label={formatSlotLabel(t, sel.weekday, sel.start_time, timeFormat)}
                />
              ))}
            </Grid>
          ) : (
            <Grid container>
              <Chip
                label={`∞ ${t('manage_passes.pass.status.unlimited')}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Grid>
          )
        ) : (
          <PunchProgress used={used} total={numPunches} />
        )}
        {pass.guest_pass_remaining != null && (
          <Typography variant="body2" color="text.secondary">
            {t('manage_passes.pass.guestRemaining', { remaining: pass.guest_pass_remaining })}
          </Typography>
        )}
        {showBuyAgain && passOrderPage && (
          <Grid container justifyContent="flex-end">
            <Button
              variant="outlined"
              size="small"
              // The whole card navigates to the pass, so the button has to keep its click.
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setBuyAgainOpen(true);
              }}
              sx={{ minHeight: 32 }}>
              {t('manage_passes.buyAgain.cta')}
            </Button>
          </Grid>
        )}
        {membership ? (
          <MembershipSummary pass={pass} passOrderPage={passOrderPage} account={account} variant="card" />
        ) : (
          (() => {
            if (pass.status === PassStatus.ACTIVE && !hasPassStarted(pass) && pass.start_date) {
              return (
                <Typography variant="body1" fontWeight={500} color="text.secondary">
                  {t('manage_passes.pass.startsOn', { date: moment(pass.start_date).format(longDateFormat) })}
                </Typography>
              );
            }
            const expDate = getExpirationDate(pass, passOrderPage);
            if (!expDate) {
              return (
                <Typography variant="body1" fontWeight={500} color="text.secondary">
                  {t('manage_passes.pass.noExpiry')}
                </Typography>
              );
            }
            const daysLeft = getDaysUntilExpiry(pass, passOrderPage);
            const isExpired = daysLeft !== null && daysLeft < 0;
            const isSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;
            const color = isExpired ? 'error.main' : isSoon ? 'warning.main' : 'text.primary';
            return (
              <Typography variant="body1" fontWeight={500} color={color}>
                {isExpired
                  ? t('manage_passes.pass.expiredOn', { date: moment(expDate).format(longDateFormat) })
                  : t('manage_passes.pass.validUntilDays', {
                      date: moment(expDate).format(longDateFormat),
                      days: daysLeft,
                    })}
              </Typography>
            );
          })()
        )}
      </Grid>
      {showBuyAgain && passOrderPage && (
        <BuyAgainSheet
          isOpen={buyAgainOpen}
          onClose={() => setBuyAgainOpen(false)}
          pass={pass}
          passOrderPage={passOrderPage}
        />
      )}
    </>
  );
};
